// Client-side CV text extraction (Evaluate/import P1). Ported from PSCraft's cvParse.ts pattern:
// mammoth is DYNAMICALLY imported so paste-only users (and everyone who never uploads a .docx) don't
// pay the bundle cost. ONLY the extracted TEXT ever leaves the browser — the worker has no upload route
// and no mammoth/pdfjs dependency.
//
// HIDDEN-TEXT DETECTION (R5.5, spec §7 T-HIDE-1). Until R5.5 this module returned RAW TEXT and discarded every
// scrap of styling, which is exactly what made the codebook's §11.3 hidden-text rule unenforceable end to end:
// white-on-white, 1pt and `w:vanish` runs arrived at the worker as ordinary prose. `extractCvDocument()` below
// now returns BOTH halves — the visible text a human would see, and the same text with the hidden regions marked
// with the `[[HIDDEN:detector]]` sentinels `sanitizeDocument()` already knows how to strip. The detector itself is
// `workers/src/lib/hiddenRegionDetect.ts`, shared with the worker exactly as `bannedOutputRegistry` is, so there
// is one definition of "hidden" in this repo and not two.
//
//   .docx  REAL detection. The archive is opened here and word/document.xml + word/styles.xml are read, so run
//          colour, size, w:vanish, w:webHidden, character-style inheritance, frame position and zero-width runs
//          are all visible. Extraction and detection are ONE WALK, so the offsets are into the text this module
//          actually returns.
//   .pdf   BEST EFFORT: size, position and zero-width glyphs, from pdf.js's own text-item geometry. Fill colour
//          and text render mode are NOT exposed by `getTextContent()`, so white-on-white and `3 Tr` invisible
//          text are a NAMED boundary rather than a silent miss. See DETECTOR_COVERAGE.
//   .txt   No styling exists, so there is nothing to detect and nothing to miss.
//
// PDF UPLOAD (E2E punch-list): pdf.js is ALSO dynamically imported, mirroring the mammoth code-splitting —
// paste-only and .docx-only users never pay the pdfjs chunk. pdf.js segments a page into positioned text RUNS,
// not lines, so each page's runs are first put back into reading order — columns, lines, blank lines between
// entries, bullets and indentation — by `pdfLayout.ts` from the run geometry (the pre-fix single-space join +
// whitespace collapse flattened a page to ONE line and interleaved two-column CVs, which is exactly what made a
// real demo CV "unreadable"). Pages are joined with a blank line ("\n\n") so page boundaries survive into the
// same downstream text flow as docx. An image-only / scanned PDF yields near-zero extracted text and surfaces as
// ScannedPdfError; a PDF whose geometry still reads as one flat line (see `looksFlat`) surfaces as
// UnsupportedFileError('pdf') so the user gets the select-all → paste steer instead of a garbage parse.

// Imported from the DETECTOR module rather than from `factExtract`, deliberately: `factExtract` re-exports these
// two for the worker's own importers, but reaching them through it would drag the worker's Cloudflare ambient
// types (`D1Database`, `KVNamespace`, …) into the browser's typecheck for no benefit.
import {
  annotateHiddenRegions,
  detectDocxHiddenRegions,
  detectPdfHiddenRegions,
  type HiddenRegionAnnotation,
  type PdfTextItem,
} from '../../../workers/src/lib/hiddenRegionDetect';
import { layoutPdfItems, looksFlat, type LayoutItem } from './pdfLayout';

/** Thrown when a file type we can't read client-side is uploaded. `kind: 'pdf'` lets the UI give a
 *  PDF-specific steer (select-all → paste, or the scanned-PDF message when text WAS extracted but too
 *  little of it to be usable); 'unsupported' is the generic case. */
export class UnsupportedFileError extends Error {
  constructor(public kind: 'pdf' | 'unsupported') {
    super(kind);
    this.name = 'UnsupportedFileError';
  }
}

/** Thrown when a .docx is the right TYPE but mammoth can't read it — a .doc saved as .docx, a Pages/Google-Docs
 *  export, a corrupt/encrypted file, or (rarely) the extractor chunk failing to load. Distinct from a truly-
 *  unexpected error so the UI can explain + steer to paste rather than show an alarming generic crash message. */
export class DocxParseError extends Error {
  constructor() {
    super('docx_parse_failed');
    this.name = 'DocxParseError';
  }
}

/** Thrown when the file IS a PDF pdf.js can open, but the extracted text is too short to be usable — the
 *  honest signal that it's a scanned/image-only PDF (no text layer), not a read failure. Distinct from
 *  UnsupportedFileError('pdf') (which means "we didn't even try") so the UI can give the more specific,
 *  honest "looks scanned" message instead of the generic "we can't read PDFs" one. */
export class ScannedPdfError extends Error {
  constructor() {
    super('pdf_looks_scanned');
    this.name = 'ScannedPdfError';
  }
}

// Below this many extracted characters, a PDF with a text layer is indistinguishable from noise (a stray
// header/footer token pdf.js still picks up off an otherwise-image page) — treat it as "no usable text",
// same floor philosophy as ImportCv's MIN_CHARS for a paste.
const PDF_MIN_CHARS = 200;

/**
 * What the import pipeline hands the rest of the app.
 *
 * TWO TEXTS, DELIBERATELY. `preview` is what a human would see if they opened the file — hidden regions removed —
 * and it is the string the confirm step renders and counts. `marked` is the same text with `[[HIDDEN:…]]`
 * sentinels around each detected region, and it is the string that travels onward, because `sanitizeDocument()`
 * needs the markers in order to strip the regions, record them, and raise `extraction_uncertain`. Showing the
 * MARKED text to the user would leak sentinel syntax into a preview; sending the PREVIEW text onward would throw
 * away the very signal the detector exists to produce. So both, named for what they are.
 */
export interface ExtractedCvDocument {
  preview: string;
  marked: string;
  annotations: HiddenRegionAnnotation[];
  /**
   * FALSE when no styling could be inspected — a .txt upload, or an archive that would not open. A caller must
   * never read `annotations: []` as "this document is clean" unless this is true. §11.3's rule cuts both ways:
   * the user is never accused, AND we never claim a confidence we do not have.
   */
  styling_inspected: boolean;
}

/** Remove the sentinels a marked string carries, leaving the visible text. Mirrors `sanitizeDocument`'s strip. */
function stripSentinels(marked: string): string {
  return marked.replace(/\[\[HIDDEN:[^\]]*\]\]([\s\S]*?)\[\[\/HIDDEN\]\]/g, '');
}

/** The text-item shape this module reads: the detector's subset plus the geometry the layout pass needs. */
type PdfItem = PdfTextItem & LayoutItem;

/**
 * Rebuild one page's text from pdf.js text items: layout reconstruction (reading order + separators) feeding
 * the hidden-region detector, so the detected offsets are into the text this function returns. Pure — exported
 * for the suite, which exercises it with synthetic items and with real pdf.js output.
 */
export function reconstructPdfPage(
  rawItems: readonly PdfItem[],
  box: { width: number; height: number },
  startCp: number,
): { text: string; annotations: HiddenRegionAnnotation[]; columns: number } {
  const layout = layoutPdfItems(rawItems, box.width);
  const { text, annotations } = detectPdfHiddenRegions(layout.items, box, startCp, layout.separators);
  return { text, annotations, columns: layout.columns };
}

async function extractPdfText(file: File): Promise<{ text: string; annotations: HiddenRegionAnnotation[] }> {
  // Dynamic import — mirrors the mammoth pattern above: only PDF uploaders pay this chunk's cost.
  const pdfjs = await import('pdfjs-dist');
  // Vite's `?url` import resolves to the built worker asset's URL at build time — the bundled-worker
  // pattern pdf.js expects in a bundler environment (no CDN fetch, no network dependency).
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.mjs?url')).default;
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

  const buf = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buf }).promise;
  const pageTexts: string[] = [];
  const annotations: HiddenRegionAnnotation[] = [];
  let cursor = 0; // code-point offset of the current page's first character in the joined document
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    const items = content.items as unknown as PdfItem[];
    // `page.view` is [x0, y0, x1, y1] in PDF units. It is READ DEFENSIVELY: a PDF whose page box we cannot
    // determine still gets size and zero-width detection, and the off-page rule is simply disabled for it
    // (an infinite box can never be exceeded) rather than run against a guessed page size — guessing would
    // manufacture off-canvas findings on perfectly ordinary documents.
    const view = (page as { view?: unknown }).view;
    const box = Array.isArray(view) && view.length >= 4 && view.every((n) => typeof n === 'number')
      ? { width: (view[2] as number) - (view[0] as number), height: (view[3] as number) - (view[1] as number) }
      : { width: Infinity, height: Infinity };
    // DETECTION RUNS ON THE RECONSTRUCTED TEXT — the layout pass hands the detector the reordered items and the
    // separators between them, so the offsets it reports are into the exact string kept below. No whitespace
    // collapse follows (it would move every offset after the first run of spaces and flatten the structure the
    // layout pass just rebuilt).
    const { text: pageText, annotations: pageAnnotations } = reconstructPdfPage(items, box, cursor);
    annotations.push(...pageAnnotations);
    pageTexts.push(pageText);
    cursor += Array.from(pageText).length + 2; // + the "\n\n" page join
  }
  const joined = pageTexts.join('\n\n');
  return { text: joined, annotations: annotations.filter((a) => a.cp_end <= Array.from(joined).length) };
}

/**
 * Extract a CV document from an uploaded file — the visible text, the marked text, and the hidden regions found.
 *
 * .docx → the shared detector's ONE-WALK extractor (text and styling together, so the offsets are sound), with
 *         mammoth kept as the FALLBACK for an archive the detector cannot open. A mammoth fallback returns text
 *         with `styling_inspected: false`, because it genuinely did not look.
 * .pdf  → pdf.js, with best-effort geometry detection; the ScannedPdfError steer is unchanged.
 * .txt  → read directly. No styling, nothing to detect.
 */
export async function extractCvDocument(file: File): Promise<ExtractedCvDocument> {
  const name = file.name.toLowerCase();

  if (name.endsWith('.docx')) {
    const buf = await file.arrayBuffer();
    let detected: Awaited<ReturnType<typeof detectDocxHiddenRegions>> | null = null;
    try {
      detected = await detectDocxHiddenRegions(buf);
    } catch {
      detected = null; // fall through to mammoth rather than failing the upload on a detector defect
    }
    if (detected && detected.styling_inspected && detected.text.trim().length > 0) {
      const marked = annotateHiddenRegions(detected.text, detected.annotations);
      return {
        preview: stripSentinels(marked).trim(),
        marked: marked.trim(),
        annotations: detected.annotations,
        styling_inspected: true,
      };
    }
    // FALLBACK. The archive would not open, or carried no text where the detector looked (an unusual export that
    // puts its body somewhere else). mammoth is more forgiving; it also cannot see styling, and the return says so.
    try {
      const mammoth = await import('mammoth/mammoth.browser');
      const r = await mammoth.extractRawText({ arrayBuffer: buf });
      const text = (r.value ?? '').trim();
      if (text.length === 0) throw new DocxParseError();
      return { preview: text, marked: text, annotations: [], styling_inspected: false };
    } catch {
      // mammoth/JSZip rejects a file that isn't valid OOXML (a .doc renamed .docx, a non-Word export, corrupt,
      // or encrypted) — and a chunk-load blip lands here too. Either way the steer is the same: paste instead.
      throw new DocxParseError();
    }
  }

  if (name.endsWith('.pdf') || file.type === 'application/pdf') {
    let extracted: { text: string; annotations: HiddenRegionAnnotation[] };
    try {
      extracted = await extractPdfText(file);
    } catch {
      // pdf.js itself failed to open the file (corrupt, encrypted, not really a PDF) — same generic PDF
      // steer as "we never tried": select-all → paste always works.
      throw new UnsupportedFileError('pdf');
    }
    const text = extracted.text.trim();
    if (text.length < PDF_MIN_CHARS) throw new ScannedPdfError(); // opened fine, but no usable text layer
    // SAFETY NET: enough text, but no recoverable LINE structure (< 8 lines, or most of it on one line) — the
    // segmenter would read that as garbage, so steer to select-all → paste (the generic PDF steer) instead.
    if (looksFlat(text)) throw new UnsupportedFileError('pdf');
    const marked = annotateHiddenRegions(extracted.text, extracted.annotations);
    return {
      preview: stripSentinels(marked).trim(),
      marked: marked.trim(),
      annotations: extracted.annotations,
      // Best effort, and the flag says the detector RAN. What it cannot see is named in DETECTOR_COVERAGE.pdf.
      styling_inspected: true,
    };
  }

  if (name.endsWith('.txt') || name.endsWith('.md') || name.endsWith('.text') || file.type.startsWith('text/')) {
    const text = (await file.text()).trim();
    return { preview: text, marked: text, annotations: [], styling_inspected: false };
  }

  throw new UnsupportedFileError('unsupported');
}

/**
 * The original string-returning entry point, kept because several callers and tests only ever wanted the text.
 * It returns the MARKED text — the string that must travel onward — so an existing caller cannot silently drop
 * the detector's output by not knowing about it. Use `extractCvDocument` when the preview matters.
 */
export async function extractCvText(file: File): Promise<string> {
  return (await extractCvDocument(file)).marked;
}
